import { Box, FormControl, Grid, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import { useState } from 'react';
import { BoardDisclaimer } from '../../components/BoardComponents/BoardDisclaimer';
import { CollapsibleTable, CollapsibleTableCell } from '../../components/CollapsibleTable';
import { DocumentHyperlink } from '../../components/DocumentHyperlink';
import { MainContainer } from '../../components/MainContainerComp';
import ScrollIndicatorsComp from '../../components/ScrollIndicatorsComp/ScrollIndicatorsComp';
import ScrollToTopButton from '../../components/ScrollTopButton';
import useVerticalScroll from '../../utils/hooks/useVerticalScroll';
import {
  DisclaimerContainerStyle,
  DisclaimerContentStyle,
  PlaceholderStyle, SelectContainerStyle, SelectMenuStyle,
  SelectStyle,
  buttonStyle,
  fixedSectionStyle,
  lastUpdateStyles,
  pluggedInStyles,
  viewSlipsContainerStyle
} from './styled';
import { ButtonItem, ISurplusBoardData } from './SurplusBoardData';
import { BOARDS } from './UnionBoardModels';

export const SurplusBoard = ({
  data,
  handleBoardSelect,
  handleButtonClick
}: {
  data: ISurplusBoardData,
  handleBoardSelect: (event: SelectChangeEvent<string>) => void,
  handleButtonClick: (button: ButtonItem) => void;
}) => {
  const selectedBoard = data.selectedBoard ?? "";
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const dropDownsFixedAtTop = useVerticalScroll();

  const cols: CollapsibleTableCell[] = [
    "",
    "Description",
    "Board",
    "Plate",
  ].map(title => ({ title, align: "left" }));

  const boardData = data.buttons.map((button) => {
    const { buttonImage, buttonDescription, boardDescription, plateNumber } = button;
    const [alt] = buttonImage.split(".");
    return {
      buttonImage: (
        <Box sx={buttonStyle}>
          <img src={`/assets/images/buttons/${buttonImage}`} alt={alt} onClick={() => handleButtonClick(button)} />
        </Box>
      ),
      buttonDescription,
      boardDescription,
      plateNumber
    }
  });

  return (
    <Box>
      <MainContainer sx={{ pb: 8 }}>
        {showDisclaimer &&
          <Box sx={DisclaimerContainerStyle}>
            <Box sx={DisclaimerContentStyle}>
              <BoardDisclaimer handleClick={() => setShowDisclaimer(false)} />
            </Box>
          </Box>
        }
        <Grid container sx={dropDownsFixedAtTop ? fixedSectionStyle : SelectContainerStyle}>
          <Grid item xs={5.5} sm={4} lg={3}>
            <FormControl fullWidth>
              <Select
                className='small'
                sx={SelectStyle}
                displayEmpty
                value={selectedBoard}
                onChange={handleBoardSelect}
                data-automation="board-dropdown"
                MenuProps={{ sx: SelectMenuStyle }}
                renderValue={(board) => {
                  if (!board) return (
                    <Typography sx={PlaceholderStyle}>
                      Select a board
                    </Typography>
                  )
                  return BOARDS.filter(b => b.code === board).map(b => b.display)
                }}
              >
                {BOARDS.map(({ code, display }) => (
                  <MenuItem key={code} value={code}>
                    {display}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sx={viewSlipsContainerStyle}>
            <DocumentHyperlink
              link={`/union-board-slips/${selectedBoard}`}
              icon={null}
              target="_self"
              dataAutomation='view-slips'
              title='View Slips'
            />
          </Grid>
          <Grid item xs={8}>
            <Typography data-automation="last-update" typography={"caption"} sx={lastUpdateStyles}>Last Update: {data.buttonUpdate} </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography data-automation="plugged-in" typography={"caption"} sx={pluggedInStyles}>Plugged-In: {data.pluggedIn ?? "N/A"} </Typography>
          </Grid>
        </Grid>
        <Box sx={{ overflow: "auto" }}>
          <Typography
            component={'div'}
            variant='container'
            data-automation="data-container"
          >
            <ScrollIndicatorsComp>
              <CollapsibleTable
                sxSmall={true}
                cols={cols}
                rows={boardData}
                isOneLevel
                dataAutomation={{
                  header: "surplus-tbl-header",
                  columns: [
                    "surplus-img",
                    "surplus-desc",
                    "surplus-board",
                    "surplus-plate"
                  ].map(x => `${x} surplus-tbl-head-cell`),
                  rows: "surplus-tbl-row"
                }}
              />
            </ScrollIndicatorsComp>
          </Typography>
        </Box>
      </MainContainer >
      <ScrollToTopButton />
    </Box >
  )
}